/**
 * Data-driven copy for /solutions/* landing pages (homepage-aligned UI).
 * Add new entries here and register the slug in `SOLUTION_LANDING_SLUGS`.
 */

export const SOLUTION_LANDING_SLUGS = ['marketing-agencies', 'marketing-teams', 'small-business', 'operations', 'finance'];

const DEFAULT_TRUST_CHIPS = ['7-day free trial', 'No credit card', 'Clear pricing'];

/** Shared How it works section — copy matches homepage (#how-it-works). */
export const SOLUTION_HOW_IT_WORKS = {
    /** Short display title (homepage-aligned) */
    displayHeadline: '8 clicks.',
    displayHeadlineAriaLabel: 'Set up your automation in eight clicks',
    subheadline: 'Set up your automation—connect once and your data stays updated automatically.',
    proofLines: ['Most setups take under 5 minutes.'],
    flowLabels: ['Your data', 'Updates automatically', 'Anywhere you need it'],
    steps: [
        {
            title: 'Connect the tools you already use',
            text: 'Log into Shopify, Facebook, QuickBooks—whatever you already use. No setup. No engineering.',
            accent: 'sky',
        },
        {
            title: 'Start with smart defaults',
            text: 'We pick the right data—you can tweak it anytime.',
            accent: 'orange',
        },
        {
            title: 'Stays up to date—automatically',
            text: 'Updates automatically. No exports. No rebuilds.',
            accent: 'sky',
        },
    ],
};

/**
 * @typedef {object} SolutionLandingPageConfig
 * @property {string} path
 * @property {string} title
 * @property {string} description
 * @property {object} hero
 * @property {object} painSection
 * @property {object} solutionSection
 * @property {object} workflowsSection
 * @property {object} finalCta
 */

export const SOLUTION_LANDING_PAGES = [
    {
        slug: 'marketing-agencies',
        path: '/solutions/marketing-agencies',
        title: 'Client Reporting Automation for Agencies | Data Connector',
        description:
            'Client reports that update themselves—show value without rebuilding dashboards every week. Connect ads, Shopify, CRMs, and Sheets.',
        hero: {
            eyebrow: 'For marketing agencies',
            headlineLine1: 'Client reports that update themselves',
            headlineAccent: '',
            lead: 'Show clients your value without rebuilding reports every week.',
            primaryCta: 'Set up your first client sync',
            secondaryCta: 'See how it works',
            secondaryToHash: '#how-it-works',
            microcopy: 'Most setups take under five minutes. No engineers required.',
            trustChips: DEFAULT_TRUST_CHIPS,
        },
        painSection: {
            title: 'Still rebuilding client reports by hand?',
            lead: '',
            guideLine: '',
            groups: [
                {
                    categoryTitle: null,
                    cards: [
                        {
                            title: 'Stop exporting ad data every week',
                            description: 'Client performance stays updated without CSVs or copy/paste.',
                            accent: 'orange',
                        },
                        {
                            title: 'Stop rebuilding reports before client calls',
                            description: 'Dashboards stay ready before the meeting starts.',
                            accent: 'sky',
                        },
                        {
                            title: 'Stop chasing numbers across every platform',
                            description: 'Ads, sales, CRM, and reporting data stay in sync.',
                            accent: 'orange',
                        },
                    ],
                },
            ],
        },
        solutionSection: {
            eyebrow: 'What Data Connector does',
            title: 'Client reporting that stays current',
            lead: 'Connect once per client. Sheets and dashboards refresh—so your team spends time on strategy, not exports.',
            bullets: [
                {
                    title: 'Meeting-ready numbers',
                    line: 'Performance lands where your templates live—refreshed before the conversation starts.',
                },
                {
                    title: 'Repeatable per client',
                    line: 'Same setup playbook—connect sources once and reuse what works.',
                },
                {
                    title: 'Fewer “send me the export” loops',
                    line: 'Clients see updated totals without chasing your team for files.',
                },
            ],
        },
        workflowsSection: {
            title: 'Common workflows',
            lead: '',
            items: [
                { title: 'Facebook Ads → Client dashboard', accent: 'orange' },
                { title: 'Google Ads → Client report', accent: 'sky' },
                { title: 'Shopify → Client sales report', accent: 'orange' },
                { title: 'CRM → Client reporting sheet', accent: 'sky' },
            ],
        },
        finalCta: {
            headline: 'Spend less time reporting. More time proving results.',
            sub: 'Set it once. Your client data updates automatically.',
            primaryCta: 'Set up your first client sync',
        },
    },
    {
        slug: 'marketing-teams',
        path: '/solutions/marketing-teams',
        title: 'Marketing Data in One Place | Data Connector',
        description:
            'Keep ads, CRM, and channel data together—updated automatically so your marketing team answers faster without manual reporting.',
        hero: {
            eyebrow: 'For marketing teams',
            headlineLine1: 'All your marketing data in one place',
            headlineAccent: '',
            lead: 'Stop jumping between platforms just to answer simple performance questions.',
            primaryCta: 'Set up your first marketing sync',
            secondaryCta: 'See how it works',
            secondaryToHash: '#how-it-works',
            microcopy: 'Most setups take under five minutes. No engineers required.',
            trustChips: DEFAULT_TRUST_CHIPS,
        },
        painSection: {
            title: 'Still pulling marketing reports manually?',
            lead: '',
            guideLine: '',
            groups: [
                {
                    categoryTitle: null,
                    cards: [
                        {
                            title: 'Stop exporting ad data every week',
                            description: 'Your campaign data stays current without CSVs.',
                            accent: 'orange',
                        },
                        {
                            title: 'Stop rebuilding leadership reports',
                            description: 'Dashboards stay updated for meetings and reviews.',
                            accent: 'sky',
                        },
                        {
                            title: 'Stop switching tabs to find answers',
                            description: 'Your marketing data stays together in one place.',
                            accent: 'orange',
                        },
                    ],
                },
            ],
        },
        solutionSection: {
            eyebrow: 'What Data Connector does',
            title: 'Marketing performance without the busywork',
            lead: 'Bring ads, CRM, and channels together—on the schedule you choose.',
            bullets: [
                {
                    title: 'Answers in one view',
                    line: 'Fewer tabs—your team sees performance where they already work.',
                },
                {
                    title: 'Ready for reviews',
                    line: 'Leadership updates stay current without last-minute rebuilds.',
                },
                {
                    title: 'Less copy/paste',
                    line: 'Numbers refresh themselves—so Monday isn’t an export marathon.',
                },
            ],
        },
        workflowsSection: {
            title: 'Common workflows',
            lead: '',
            items: [
                { title: 'Facebook Ads → Dashboard', accent: 'orange' },
                { title: 'Google Ads → Google Sheets', accent: 'sky' },
                { title: 'CRM → Marketing report', accent: 'orange' },
                { title: 'Email platform → Dashboard', accent: 'sky' },
            ],
        },
        finalCta: {
            headline: 'Give your team answers without manual reporting.',
            sub: 'Connect once. Marketing metrics stay updated.',
            primaryCta: 'Set up your first marketing sync',
        },
    },
    {
        slug: 'small-business',
        path: '/solutions/small-business',
        title: 'Automated Business Data Sync | Data Connector',
        description:
            'Keep sales, orders, customers, and reports updated automatically—less spreadsheet work for small businesses.',
        hero: {
            eyebrow: 'For small business',
            headlineLine1: 'Stop running your business',
            headlineAccent: 'in spreadsheets',
            lead: 'Keep your sales, orders, customers, and reports updated automatically.',
            primaryCta: 'Set up your first sync',
            secondaryCta: 'See how it works',
            secondaryToHash: '#how-it-works',
            microcopy: 'Most setups take under five minutes. No engineers required.',
            trustChips: DEFAULT_TRUST_CHIPS,
        },
        painSection: {
            title: 'Still copying data between apps?',
            lead: '',
            guideLine: '',
            groups: [
                {
                    categoryTitle: null,
                    cards: [
                        {
                            title: 'Stop retyping orders into QuickBooks',
                            description: 'Sales sync automatically—no duplicate entry.',
                            accent: 'orange',
                        },
                        {
                            title: 'Stop copying orders into spreadsheets',
                            description: 'Orders show up automatically—no manual updates.',
                            accent: 'sky',
                        },
                        {
                            title: 'Stop rebuilding the same report every week',
                            description: 'Your reports stay up to date without extra work.',
                            accent: 'orange',
                        },
                    ],
                },
            ],
        },
        solutionSection: {
            eyebrow: 'What Data Connector does',
            title: 'Your tools stay aligned—automatically',
            lead: 'Orders and payments flow where you need them—without retyping or reconciling by hand.',
            bullets: [
                {
                    title: 'Sales land where you work',
                    line: 'Connect Shopify, Stripe, QuickBooks, and Sheets once—updates keep flowing.',
                },
                {
                    title: 'Fewer duplicate entries',
                    line: 'The same numbers appear across tools without manual bridges.',
                },
                {
                    title: 'Reports that refresh themselves',
                    line: 'Weekly views stay current—without rebuilding the same sheet.',
                },
            ],
        },
        workflowsSection: {
            title: 'Common workflows',
            lead: '',
            items: [
                { title: 'Shopify → QuickBooks', accent: 'orange' },
                { title: 'Shopify → Google Sheets', accent: 'sky' },
                { title: 'Stripe → QuickBooks', accent: 'orange' },
                { title: 'Orders → Weekly report', accent: 'sky' },
            ],
        },
        finalCta: {
            headline: 'Set it once. Your business data updates itself.',
            sub: 'Less manual work. More time running the business.',
            primaryCta: 'Set up your first sync',
        },
    },
    {
        slug: 'operations',
        path: '/solutions/operations',
        title: 'Operations Data Sync | Data Connector',
        description:
            'Keep CRM, orders, and internal tools aligned automatically—fewer mismatched records and manual handoffs.',
        hero: {
            eyebrow: 'For operations teams',
            headlineLine1: 'Keep your systems in sync automatically',
            headlineAccent: '',
            lead: 'Stop fixing mismatched data across your tools.',
            primaryCta: 'Set up your first workflow',
            secondaryCta: 'See how it works',
            secondaryToHash: '#how-it-works',
            microcopy: 'Most setups take under five minutes. No engineers required.',
            trustChips: DEFAULT_TRUST_CHIPS,
        },
        painSection: {
            title: 'Still entering the same data twice?',
            lead: '',
            guideLine: '',
            groups: [
                {
                    categoryTitle: null,
                    cards: [
                        {
                            title: 'Stop duplicate customer entry',
                            description: 'Customer data stays aligned across your systems.',
                            accent: 'orange',
                        },
                        {
                            title: 'Stop chasing mismatched records',
                            description: 'Your tools stay updated from the same source.',
                            accent: 'sky',
                        },
                        {
                            title: 'Stop manual handoffs between teams',
                            description: 'The right data gets where it needs to go.',
                            accent: 'orange',
                        },
                    ],
                },
            ],
        },
        solutionSection: {
            eyebrow: 'What Data Connector does',
            title: 'Cleaner handoffs between teams',
            lead: 'Operations sees current customer and order data—without spreadsheet bridges.',
            bullets: [
                {
                    title: 'Aligned records',
                    line: 'CRM, orders, and reporting tools reflect the same totals.',
                },
                {
                    title: 'Less back-and-forth',
                    line: 'Teams spend less time reconciling “which number is right?”',
                },
                {
                    title: 'Operational reporting stays current',
                    line: 'Dashboards and sheets refresh on your schedule.',
                },
            ],
        },
        workflowsSection: {
            title: 'Common workflows',
            lead: '',
            items: [
                { title: 'CRM → Google Sheets', accent: 'orange' },
                { title: 'Orders → Operations report', accent: 'sky' },
                { title: 'Customer data → Internal dashboard', accent: 'orange' },
                { title: 'Sales data → Reporting tool', accent: 'sky' },
            ],
        },
        finalCta: {
            headline: 'Keep every team working from updated data.',
            sub: 'One setup. Continuous updates across tools.',
            primaryCta: 'Set up your first workflow',
        },
    },
    {
        slug: 'finance',
        path: '/solutions/finance',
        title: 'Finance Data Automation | Data Connector',
        description:
            'Keep sales, payments, and accounting updated automatically—less manual reconciliation and spreadsheet risk.',
        hero: {
            eyebrow: 'For finance teams',
            headlineLine1: 'Numbers you can trust without manual reconciliation',
            headlineAccent: '',
            lead: 'Keep sales, payments, and accounting data updated automatically.',
            primaryCta: 'Set up your first finance sync',
            secondaryCta: 'See how it works',
            secondaryToHash: '#how-it-works',
            microcopy: 'Most setups take under five minutes. No engineers required.',
            trustChips: DEFAULT_TRUST_CHIPS,
        },
        painSection: {
            title: 'Still reconciling data by hand?',
            lead: '',
            guideLine: '',
            groups: [
                {
                    categoryTitle: null,
                    cards: [
                        {
                            title: 'Stop retyping sales into accounting',
                            description: 'Sales data syncs automatically into your finance tools.',
                            accent: 'orange',
                        },
                        {
                            title: 'Stop chasing payment data',
                            description: 'Revenue and payment activity stay updated.',
                            accent: 'sky',
                        },
                        {
                            title: 'Stop fixing spreadsheet mistakes',
                            description: 'Reports stay current without manual copy/paste.',
                            accent: 'orange',
                        },
                    ],
                },
            ],
        },
        solutionSection: {
            eyebrow: 'What Data Connector does',
            title: 'Finance-ready numbers that stay current',
            lead: 'Sales and payments land where finance works—updated on a schedule you control.',
            bullets: [
                {
                    title: 'Cleaner books',
                    line: 'Less retyping between storefronts, payments, and accounting.',
                },
                {
                    title: 'Payment clarity',
                    line: 'Revenue activity stays visible without chasing exports.',
                },
                {
                    title: 'Fewer spreadsheet risks',
                    line: 'Reporting views refresh—without fragile manual updates.',
                },
            ],
        },
        workflowsSection: {
            title: 'Common workflows',
            lead: '',
            items: [
                { title: 'Shopify → QuickBooks', accent: 'orange' },
                { title: 'Stripe → QuickBooks', accent: 'sky' },
                { title: 'Orders → Finance report', accent: 'orange' },
                { title: 'Sales → Google Sheets', accent: 'sky' },
            ],
        },
        finalCta: {
            headline: 'Spend less time reconciling. More time trusting the numbers.',
            sub: 'Automated updates across sales, payments, and accounting.',
            primaryCta: 'Set up your first finance sync',
        },
    },
];

/** @type {Record<string, typeof SOLUTION_LANDING_PAGES[number]>} */
export const SOLUTION_LANDING_PAGES_BY_SLUG = Object.fromEntries(SOLUTION_LANDING_PAGES.map((p) => [p.slug, p]));
